import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Plus, Edit, Trash, CalendarIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn, safeSaveText } from '@/lib/utils';

interface Section {
  id: string;
  template_name: string;
  display_order: number;
  target_date: string | null;
  countdown_enabled: boolean;
  template_type: 'sample' | 'event' | 'blog';
  is_published: boolean;
}

export const AdminSectionManager = () => {
  const [sections, setSections] = useState<Section[]>([]);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [newSectionName, setNewSectionName] = useState('');
  const [newTargetDate, setNewTargetDate] = useState<Date | undefined>(undefined);
  const [newCountdownEnabled, setNewCountdownEnabled] = useState(false);
  const [newTemplateType, setNewTemplateType] = useState<'sample' | 'event' | 'blog'>('event');
  const [newIsPublished, setNewIsPublished] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const loadSections = async () => {
    try {
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .order('display_order');
      
      if (error) throw error;
      setSections((data as any[]) || []);
    } catch (error) {
      // Errors are logged in development only via secureLog
      toast({
        title: "Error",
        description: "Failed to load sections",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSections();
  }, []);

  const createSection = async () => {
    if (!newSectionName.trim()) return;
    
    try {
      const maxOrder = Math.max(...sections.map(s => s.display_order), -1);
      const { error } = await supabase
        .from('templates')
        .insert({
          template_name: safeSaveText(newSectionName, 100),
          display_order: maxOrder + 1,
          target_date: newTargetDate ? newTargetDate.toISOString().split('T')[0] : null,
          countdown_enabled: newCountdownEnabled,
          template_type: newTemplateType,
          is_published: newIsPublished
        });
      
      if (error) throw error;
      
      setNewSectionName('');
      setNewTargetDate(undefined);
      setNewCountdownEnabled(false);
      setNewTemplateType('event');
      setNewIsPublished(false);
      loadSections();
      toast({
        title: "Success",
        description: "Event created successfully"
      });
    } catch (error) {
      // Errors are logged in development only via secureLog
      toast({
        title: "Error",
        description: "Failed to create event",
        variant: "destructive"
      });
    }
  };

  const updateSection = async (section: Section) => {
    try {
      const { error } = await supabase
        .from('templates')
        .update({
          template_name: safeSaveText(section.template_name, 100),
          target_date: section.target_date,
          countdown_enabled: section.countdown_enabled,
          template_type: section.template_type,
          is_published: section.is_published
        })
        .eq('id', section.id);
      
      if (error) throw error;
      
      setEditingSection(null);
      loadSections();
      toast({
        title: "Success",
        description: "Event updated successfully"
      });
    } catch (error) {
      // Errors are logged in development only via secureLog
      toast({
        title: "Error",
        description: "Failed to update event",
        variant: "destructive"
      });
    }
  };

  const deleteSection = async (sectionId: string) => {
    if (!confirm('Are you sure? This will delete all phases and items in this event.')) return;
    
    try {
      const { error } = await supabase
        .from('templates')
        .delete()
        .eq('id', sectionId);
      
      if (error) throw error;
      
      loadSections();
      toast({
        title: "Success",
        description: "Event deleted successfully"
      });
    } catch (error) {
      // Errors are logged in development only via secureLog
      toast({
        title: "Error",
        description: "Failed to delete event",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading events...</div>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Manage Events
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div>
              <Label htmlFor="new-event-name">Event Name</Label>
              <Input
                id="new-event-name"
                placeholder="New event name..."
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && createSection()}
              />
            </div>
            
            <div>
              <Label>Target Date & Time</Label>
              <DateTimePicker
                value={newTargetDate || null}
                onChange={(date) => setNewTargetDate(date || undefined)}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="new-countdown-enabled"
                checked={newCountdownEnabled}
                onCheckedChange={setNewCountdownEnabled}
              />
              <Label htmlFor="new-countdown-enabled">Enable Countdown</Label>
            </div>
            
            <div>
              <Label htmlFor="new-template-type">Template Type</Label>
              <Select value={newTemplateType} onValueChange={(value: 'sample' | 'event') => setNewTemplateType(value)}>
                <SelectTrigger id="new-template-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sample">Sample</SelectItem>
                  <SelectItem value="event">Event</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Switch
                id="new-is-published"
                checked={newIsPublished}
                onCheckedChange={setNewIsPublished}
              />
              <Label htmlFor="new-is-published">Published</Label>
              <p className="text-xs text-muted-foreground ml-2">Only published event templates will be visible to users</p>
            </div>
            
            <Button onClick={createSection} disabled={!newSectionName.trim()} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              Add Event
            </Button>
          </div>

          <div className="space-y-3">
            {sections.map((section) => (
              <Card key={section.id} className="p-4">
                {editingSection?.id === section.id ? (
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor={`name-${section.id}`}>Event Name</Label>
                      <Input
                        id={`name-${section.id}`}
                        value={editingSection.template_name}
                        onChange={(e) => setEditingSection({
                          ...editingSection,
                          template_name: e.target.value
                        })}
                      />
                    </div>
                    
                    <div>
                      <Label>Target Date & Time</Label>
                      <DateTimePicker
                        value={editingSection.target_date ? new Date(editingSection.target_date) : null}
                        onChange={(date) => setEditingSection({
                          ...editingSection,
                          target_date: date ? date.toISOString() : null
                        })}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`countdown-${section.id}`}
                        checked={editingSection.countdown_enabled}
                        onCheckedChange={(checked) => setEditingSection({
                          ...editingSection,
                          countdown_enabled: checked
                        })}
                      />
                      <Label htmlFor={`countdown-${section.id}`}>Enable Countdown</Label>
                    </div>

                    <div>
                      <Label htmlFor={`template-type-${section.id}`}>Template Type</Label>
                      <Select 
                        value={editingSection.template_type} 
                        onValueChange={(value: 'sample' | 'event') => setEditingSection({
                          ...editingSection,
                          template_type: value
                        })}
                      >
                        <SelectTrigger id={`template-type-${section.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sample">Sample</SelectItem>
                          <SelectItem value="event">Event</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`is-published-${section.id}`}
                        checked={editingSection.is_published}
                        onCheckedChange={(checked) => setEditingSection({
                          ...editingSection,
                          is_published: checked
                        })}
                      />
                      <Label htmlFor={`is-published-${section.id}`}>Published</Label>
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={() => updateSection(editingSection)}>
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => setEditingSection(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-normal">{section.template_name}</h4>
                          <Badge variant={section.template_type === 'sample' ? 'secondary' : 'default'}>
                            {section.template_type}
                          </Badge>
                          {section.is_published && (
                            <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                              Published
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Order: {section.display_order} | 
                          {section.target_date ? ` Target: ${new Date(section.target_date).toLocaleDateString()}` : ' No target date'} |
                          {section.countdown_enabled ? ' Countdown enabled' : ' Countdown disabled'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingSection(section)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteSection(section.id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Date Time Picker Component for Admin
const DateTimePicker = ({ value, onChange }: { value: Date | null; onChange: (date: Date | null) => void }) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(value || undefined);
  const [selectedTime, setSelectedTime] = useState('07:00');
  const [selectedTimezone, setSelectedTimezone] = useState('America/Chicago');
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    if (value) {
      setSelectedDate(value);
      setSelectedTime(format(value, 'HH:mm'));
    }
  }, [value]);

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const handleSaveDateTime = () => {
    if (selectedDate) {
      try {
        const [hours, minutes] = selectedTime.split(':').map(Number);
        const localDate = new Date(selectedDate);
        localDate.setHours(hours, minutes, 0, 0);
        
        const utcDate = fromZonedTime(localDate, selectedTimezone);
        onChange(utcDate);
        setShowPicker(false);
      } catch (error) {
        // Errors are logged in development only via secureLog
      }
    }
  };

  const handleClear = () => {
    onChange(null);
    setSelectedDate(undefined);
    setShowPicker(false);
  };

  return (
    <Popover open={showPicker} onOpenChange={setShowPicker}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "PPP 'at' p") : "Pick a date and time"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-4 space-y-4">
          <CalendarComponent
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            initialFocus
          />
          {selectedDate && (
            <div className="space-y-3 border-t pt-3">
              <div className="space-y-2">
                <Label htmlFor="admin-time">Time</Label>
                <Input
                  id="admin-time"
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="w-full"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-timezone">Timezone</Label>
                <Select value={selectedTimezone} onValueChange={setSelectedTimezone}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                    <SelectItem value="UTC">UTC</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveDateTime} className="flex-1">
                  Set Date & Time
                </Button>
                <Button variant="outline" onClick={handleClear}>
                  Clear
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};